import FilterListIcon from "@mui/icons-material/FilterList";
import { ReactComponent as RulesetIcon } from "../../../assets/ruleset.svg";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePortal } from "../../../context/PortalsContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import DataTable, {
  ColumnDef,
  SortOrder,
} from "../../../components/DataTable/DataTable";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { Owners } from "../../../context/PortalsContext";
import { useToast } from "../../../context/ToastContext";
import {
  getAssignments,
  updateIsAssigned,
} from "../../../services/api/apiClient";
import {
  Assignment,
  AssignmentResponse,
  AssignmentType,
  GetAssignmentParams,
  PackageType,
  Status,
} from "../../../services/api/types/assignments/assignment";
import { brand, gray } from "../../../theme/themePrimitives";
import {
  AssignmentsFilter,
  FilterType,
} from "../../Home/components/MyAssignments/components/AssignmentsFilter";
import { extractErrorMessage } from "../../../utils/errorUtils";

interface AssignmentManagementProps {}

export const AssignmentManagement: React.FC<AssignmentManagementProps> = () => {
  // Starts true so the totalCount inflation below covers a restored page
  // before the first fetch resolves (the DataTable out-of-range guard would
  // otherwise reset it); the initial fetch always runs on mount.
  const [loading, setLoading] = useState(true);
  const [assignmentResponse, setAssignmentResponse] =
    useState<AssignmentResponse>();
  const [portalsArray, setPortalsArray] = useState<Assignment[]>([]);
  const [courseToggleModal, setCourseToggleModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedCourseModal, setSelectedCourseModal] = useState<Assignment>();
  const [selectedOwners, setSelectedOwners] = useState<Owners[]>([]);
  const { portals } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();
  const { portalUid } = useParams<{ portalUid: string }>();
  const showLastPage =
    (location.state as { showLastPage?: boolean })?.showLastPage ?? false;
  // Return to the page the admin was on when they opened an assignment for
  // editing (stored on row click) — no lookup fetch needed.
  const [page, setPage] = useState(() => {
    const savedPage = Number(
      sessionStorage.getItem("assignmentSearchReturnPage"),
    );
    sessionStorage.removeItem("assignmentSearchReturnPage");
    return savedPage > 0 ? savedPage : 1;
  });
  const { showToast } = useToast();
  const [limit, setLimit] = useState(10);
  const [showFilter, setShowFilter] = useState(false);
  const [assignmentSearch, setAssignmentSearch] = useState<string>("");

  const [sort, setSort] = useState<
    { orderBy: string; order: SortOrder } | undefined
  >(undefined);
  const [filtersList, setFiltersList] = useState<FilterType[]>([]);

  const [filtersListOptimized, setFiltersListOptimized] = useState<
    FilterType[]
  >([]);
  const [showChips, setShowChips] = useState(false);
  const [hasSeletedFilter, setHasSeletedFilter] = useState(true);
  const hasProcessedShowLastPage = useRef(false);
  const [shouldClearLocationState, setShouldClearLocationState] =
    useState(false);
  const isClearingFilters = useRef(false);
  const isUpdatingFilters = useRef(false);
  const isInitialSearchEffect = useRef(true);
  const filtersSnapshot = useRef<FilterType[]>([]);
  const filtersOptimizedSnapshot = useRef<FilterType[]>([]);

  const fetchAssignments = useCallback(
    async (extraParams: Partial<GetAssignmentParams> = {}) => {
      const params = new URLSearchParams(window.location.search);

      const URLParams: GetAssignmentParams = {
        limit: extraParams.limit ?? limit,
        offset: extraParams.offset ?? (page - 1) * limit,
        search: extraParams.search ?? (assignmentSearch || undefined),
        ...extraParams,
      };

      // Conditionally add other params if they exist
      const search = params.get("search");
      const packageType = params.get("packageType");
      const status = params.get("status");
      const owner = params.get("owner");
      const assignmentType = params.get("assignmentType");

      assignmentType &&
        (URLParams.assignmentType = assignmentType as AssignmentType);
      if (search) {
        URLParams.search = search;
      }

      packageType && (URLParams.packageType = packageType as PackageType);

      status && (URLParams.status = status as Status);

      owner && (URLParams.owner = owner);

      // Add sorting if present
      if (sort) {
        URLParams.orderBy = sort.orderBy;
        URLParams.sort = sort.order;
      }

      // Only use showLastPage once, on the first fetch
      const shouldShowLastPage =
        showLastPage && !hasProcessedShowLastPage.current;
      if (shouldShowLastPage) {
        URLParams.showLastPage = true;
      }
      try {
        setLoading(true);
        if (!portals.selectedPortal) {
          return;
        }
        const response: AssignmentResponse = await getAssignments(
          portals.selectedPortal.portalUid,
          URLParams,
        );
        setAssignmentResponse(response);
        setPortalsArray(response.rows);
        if (shouldShowLastPage) {
          // API returns the current total count (including the new assignment).
          // Use ceil(count/limit), not ceil((count+1)/limit), or multiples of limit
          // land on a non-existent next page (empty table).
          const calculatedPage = Math.max(
            1,
            Math.ceil((response?.count ?? 0) / limit),
          );
          setPage(calculatedPage);
          hasProcessedShowLastPage.current = true;
          // Mark that we should clear the location state
          setShouldClearLocationState(true);
        }
      } catch (error: any) {
        console.log("Error:", error);
        if (error.response?.data === "No user attributes found!") return;
        showToast(
          extractErrorMessage(error, "Failed to fetch assignments"),
          "error",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      limit,
      page,
      assignmentSearch,
      portals.selectedPortal,
      showLastPage,
      showToast,
      sort,
    ],
  );

  const updatedIsAssigned = useCallback(
    async (assignmentId: string, isAssigned: boolean, userId: string) => {
      const isAssignedConverted = !isAssigned;
      try {
        if (isAssigned) {
          setModalLoading(true);
        }
        if (!portals.selectedPortal) {
          return;
        }
        await updateIsAssigned(
          portals.selectedPortal.portalUid,
          assignmentId,
          isAssignedConverted,
        );
      } catch (error: any) {
        console.log("Error:", error);

        if (error.response?.data === "Unable to update the Course") return;
        showToast(error.response?.data || "An error occurred", "error");
      } finally {
        setModalLoading(false);
        setCourseToggleModal(false);
        fetchAssignments();
      }
    },
    [portals.selectedPortal, showToast, fetchAssignments],
  );
  const handleRowsChange = (rows: number) => {
    setLimit(rows);
    setPage(1);
  };

  const getFilteredData = () => {
    const updatedFilters = [...filtersList];
    // set params using filter checkboxes values
    const getCheckedValues = (title: string) => {
      const group = updatedFilters.find((f) => f.title === title);
      if (!group) return [];
      return group.items
        .filter((item) => item.checked)
        .map((item) => item.name);
    };
    const selectedTypes = getCheckedValues("Type");
    const selectedStatus = getCheckedValues("Status");
    const selectedOwners = getCheckedValues("Owner");
    const selectedAssignmentType = getCheckedValues("Assignment Type");

    let assignmentType: AssignmentType | undefined = undefined;
    let packageType: PackageType | undefined = undefined;
    let status: Status | undefined = undefined;
    let owner: string | undefined = undefined;
    const URLParams = new URLSearchParams(window.location.search);

    assignmentType = selectedAssignmentType.join(",") as
      | AssignmentType
      | undefined;

    if (assignmentType !== undefined && assignmentType.length > 0) {
      URLParams.set("assignmentType", assignmentType);
    }

    packageType = selectedTypes.join(",") as PackageType | undefined;
    if (packageType !== undefined && packageType.length > 0) {
      URLParams.set("packageType", packageType);
    }

    status = selectedStatus.join(",") as Status | undefined;
    if (status !== undefined && status.length > 0) {
      URLParams.set("status", status);
    }

    owner = selectedOwners.join(",") || undefined;
    if (owner !== undefined && owner.length > 0) {
      URLParams.set("owner", owner);
    }

    if (assignmentSearch && assignmentSearch.trim()) {
      URLParams.set("search", assignmentSearch);
    } else {
      URLParams.delete("search");
    }
    if (URLParams.size > 0) {
      window.history.pushState({}, "", `?${URLParams.toString()}`);
    } else {
      window.history.pushState({}, "", window.location.pathname);
    }

    isUpdatingFilters.current = true;
    setPage(1);
    fetchAssignments({
      offset: 0,
      search:
        assignmentSearch && assignmentSearch.trim()
          ? assignmentSearch
          : undefined,
    }).finally(() => {
      isUpdatingFilters.current = false;
    });
  };

  const saveFilters = () => {
    const params = new URLSearchParams(window.location.search);
    const updatedFilters = [...filtersList];

    updatedFilters.forEach((group) => {
      let checkedItems: string[] = [];

      checkedItems = group.items
        .filter((item) => item.checked)
        .map((item) => item.name);
      let groupTitle = "";

      if (group.title === "Type") {
        groupTitle = "packageType";
      } else if (group.title === "Assignment Type") {
        groupTitle = "assignmentType";
      } else {
        groupTitle = group.title.toLowerCase();
      }
      if (checkedItems.length > 0) {
        params.set(groupTitle, checkedItems.join(","));
      } else {
        params.delete(groupTitle);
      }
    });

    if (params.size > 0) {
      window.history.pushState({}, "", `?${params.toString()}`);
    } else {
      window.history.pushState({}, "", window.location.pathname);
    }

    setShowChips(true);
    setShowFilter(false);
    getFilteredData();
  };

  const clearFilters = () => {
    const params = new URLSearchParams(window.location.search);
    const updatedFilters = [...filtersList];
    updatedFilters.map((filter) =>
      filter.items.map((item) => (item.checked = false)),
    );
    params.delete("packageType");
    params.delete("status");
    params.delete("owner");
    params.delete("assignmentType");
    setShowChips(false);
    window.history.pushState({}, "", window.location.pathname);
    isClearingFilters.current = true;
    setPage(1);
    setFiltersList(updatedFilters);
    setHasSeletedFilter(true);
    Promise.resolve(fetchAssignments({ offset: 0 })).finally(() => {
      isClearingFilters.current = false;
    });
  };

  const checkHasParams = useCallback(() => {
    const updatedFilters = [...filtersList];
    const filtersChecked = updatedFilters.some((filter) =>
      filter.items.some((item) => item.checked),
    );
    return filtersChecked;
  }, [filtersList]);

  const handleCheckboxChange = (index: number, itemIndex: number) => {
    const updatedFilters = [...filtersList];
    const updatedFiltersListOptimized = [...filtersListOptimized];

    if (updatedFilters[index].title === "Owner") {
      updatedFiltersListOptimized[index].items[itemIndex].checked =
        !updatedFiltersListOptimized[index].items[itemIndex].checked;
      updatedFilters[index].items[itemIndex].checked =
        !updatedFilters[index].items[itemIndex].checked;
      setFiltersListOptimized(updatedFiltersListOptimized);
    } else {
      updatedFilters[index].items[itemIndex].checked =
        !updatedFilters[index].items[itemIndex].checked;
    }

    setFiltersList(updatedFilters);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSortChange = (orderBy: string, order: SortOrder) => {
    setSort({ orderBy, order });
  };

  const confirmModalButtonClick = () => {
    if (selectedCourseModal) {
      updatedIsAssigned(
        selectedCourseModal.assignmentUid,
        selectedCourseModal.isAssigned,
        selectedCourseModal.portalUid,
      );
    }
  };

  const setCurrentCourse = useCallback(
    (assignmentUid: string | null) => {
      if (assignmentUid) {
        const selectedCourse = portalsArray.find(
          (course) => course.assignmentUid === assignmentUid,
        );
        if (selectedCourse) {
          setSelectedCourseModal(selectedCourse);
        }
      }
    },
    [portalsArray],
  );

  const handleCourseNameChange = useCallback(
    (portal: Assignment) => {
      if (!portal.isAssigned) {
        updatedIsAssigned(
          portal.assignmentUid,
          portal.isAssigned,
          portal.portalUid,
        );
      } else {
        setCourseToggleModal(true);
      }
    },
    [updatedIsAssigned],
  );

  const handleNavigateToRules = useCallback(
    (assignment: Assignment) => {
      navigate(
        `/portal-configuration/${portals.selectedPortal?.portalUid}/assignment-search/${assignment.assignmentUid}/rules`,
        {
          state: {
            assignmentTitle: assignment.title,
            assignmentUid: assignment.assignmentUid,
          },
        },
      );
    },
    [navigate, portals.selectedPortal?.portalUid],
  );

  const handleNavigateToAssignmentUploads = useCallback(
    (assignment: Assignment) => {
      if (!assignment.isAddedToPortal) {
        showToast("Assignment isn't assigned to the portal", "error");
        return;
      }
      localStorage.setItem("portalOwner", assignment.ownerUid);
      sessionStorage.setItem("assignmentSearchReturnPage", String(page));
      navigate(
        `/portal-configuration/${portals.selectedPortal?.portalUid}/assignment-uploads/${assignment.assignmentUid}/edit`,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, portals.selectedPortal?.portalUid, showToast, page],
  );

  const handleModalConfirm = () => {
    setModalLoading(false);
  };

  const handleChipDelete = (itemIndex: number, iIndex: number) => {
    setFiltersList((prev) =>
      prev.map((item, idx) => {
        if (idx !== itemIndex) return item;
        return {
          ...item,
          items: item.items.map((i, iIdx) =>
            iIdx === iIndex ? { ...i, checked: !i.checked } : i,
          ),
        };
      }),
    );
    const params = new URLSearchParams(window.location.search);

    let clickedTitle = filtersList[itemIndex]?.title;

    // // delete current chip from params
    let ChipParams = params;
    if (clickedTitle === "Type") {
      clickedTitle = "packageType";
    } else if (clickedTitle === "Assignment Type") {
      clickedTitle = "assignmentType";
    } else {
      clickedTitle = clickedTitle.toLowerCase();
    }

    const paramType = ChipParams.get(clickedTitle);

    if (paramType) {
      const arrayedParamType = paramType.split(",");
      const updatedParamType = arrayedParamType.filter(
        (item) => item !== filtersList[itemIndex].items[iIndex].name,
      );

      if (updatedParamType.length > 0) {
        ChipParams.set(clickedTitle, updatedParamType.join(","));
      } else {
        ChipParams.delete(clickedTitle);
      }
    }
    if (ChipParams.size > 0) {
      window.history.pushState({}, "", `?${ChipParams.toString()}`);
    } else {
      window.history.pushState({}, "", window.location.pathname);
    }

    isUpdatingFilters.current = true;
    setPage(1);
    fetchAssignments({ offset: 0 }).finally(() => {
      isUpdatingFilters.current = false;
    });

    if (ChipParams.size <= 1) {
      setHasSeletedFilter(true);
    }
  };

  const renderChips = () =>
    filtersListOptimized.map((item, itemIndex) =>
      item.items.map((i, iIndex) =>
        i.checked ? (
          <Chip
            key={`${item.title}-${i.name}`}
            label={`${item.title}: ${i.name}`}
            onDelete={() => handleChipDelete(itemIndex, iIndex)}
            sx={{
              backgroundColor: brand[600],
              border: `1px solid ${gray[300]}`,
              padding: "16px 8px",
              borderRadius: "6px",
              "& .MuiChip-label": {
                color: "white",
              },
            }}
          />
        ) : null,
      ),
    );

  useEffect(() => {
    const ownersFilter = localStorage.getItem("selectedOwnersFilter");
    if (ownersFilter) {
      try {
        const parsedData = JSON.parse(ownersFilter);
        if (Array.isArray(parsedData)) {
          setSelectedOwners(parsedData);
        }
      } catch (error: any) {
        showToast("Error parsing Selected Owners from localStorage", error);
      }
    }
  }, [loading, showToast]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const types = Object.values(PackageType);
    const statuses = Object.values(Status).filter((s) => s !== Status.DRAFT);
    const assignmentType = Object.values(AssignmentType);

    const packageType = params.get("packageType");
    const status = params.get("status");
    const owner = params.get("owner");
    const assignmentTypeParam = params.get("assignmentType");

    const arrayedAssignmentType = assignmentTypeParam?.split(",") || [];
    const arrayedPackageType = packageType?.split(",") || [];
    const arrayedStatus = status?.split(",") || [];
    const arrayedOwner = owner?.split(",") || [];

    const filters: FilterType[] = [
      {
        title: "Type",
        items: types.map((t, i) => ({
          id: `type-item-${i}`,
          name: t,
          checked: arrayedPackageType.includes(t),
        })),
      },
      {
        title: "Status",
        items: statuses.map((s, i) => ({
          id: `status-item-${i}`,
          name: s,
          checked: arrayedStatus.includes(s),
        })),
      },
      {
        title: "Assignment Type",
        items: assignmentType.map((s, i) => ({
          id: `user-assignment-item-${i}`,
          name: s,
          checked: arrayedAssignmentType.includes(s),
        })),
      },
      {
        title: "Owner",
        items: selectedOwners.map((t, i) => ({
          id: `owner-item-${i}`,
          name: t.portalUid,
          checked: arrayedOwner.includes(t.portalUid),
        })),
      },
    ];

    setFiltersList(filters);
    const optimizedList = filters.map((f) =>
      f.title === "Owner"
        ? {
            ...f,
            items: selectedOwners.map((t, i) => ({
              id: `owner-item-${i}`,
              name: t.name,
              checked: arrayedOwner.includes(t.portalUid),
            })),
          }
        : f,
    );

    setFiltersListOptimized(optimizedList);
    setShowChips(true);
  }, [portalsArray, selectedOwners]);

  useEffect(() => {
    if (showFilter) {
      filtersSnapshot.current = JSON.parse(JSON.stringify(filtersList));
      filtersOptimizedSnapshot.current = JSON.parse(
        JSON.stringify(filtersListOptimized),
      );
    }
  }, [showFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterDrawerClose = () => {
    setFiltersList(filtersSnapshot.current);
    setFiltersListOptimized(filtersOptimizedSnapshot.current);
    setShowFilter(false);
  };

  // Reset the showLastPage flag when portal changes or showLastPage becomes false
  useEffect(() => {
    if (!showLastPage) {
      hasProcessedShowLastPage.current = false;
    }
  }, [showLastPage, portals.selectedPortal?.portalUid]);

  // Clear location state after processing showLastPage
  useEffect(() => {
    if (shouldClearLocationState) {
      navigate(location.pathname + location.search, { replace: true });
      setShouldClearLocationState(false);
    }
  }, [shouldClearLocationState, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isClearingFilters.current && !isUpdatingFilters.current) {
      fetchAssignments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, page, sort, portals.selectedPortal]);

  useEffect(() => {
    if (isInitialSearchEffect.current) {
      isInitialSearchEffect.current = false;
      return;
    }
    getFilteredData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentSearch]);

  useEffect(() => {
    if (showFilter) return;
    const filtersChecked = checkHasParams();
    setHasSeletedFilter(!filtersChecked);
  }, [checkHasParams, showFilter]);

  const columns: ColumnDef<Assignment>[] = useMemo(
    () => [
      {
        id: "title",
        label: "Course Name",
        sortable: true,
        render: (row) => (
          <Box display="flex" alignItems="center">
            <Switch
              sx={{ mr: 2 }}
              checked={row.isAssigned}
              onChange={() => {
                setCurrentCourse(row.assignmentUid);
                handleCourseNameChange(row);
              }}
            />
            <Tooltip
              placement="right"
              title={
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        wordBreak: "break-word",
                        wordWrap: "break-word",
                      }}
                      component="h5"
                    >
                      {selectedCourseModal?.title}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={0.5}>
                    {[
                      {
                        label: "Thumbnail title",
                        value: selectedCourseModal?.titleShort,
                        valueVariant: "body2" as const,
                      },
                      {
                        label: "Package type",
                        value: selectedCourseModal?.packageType,
                        valueVariant: "body2" as const,
                      },
                      {
                        label: "SKU",
                        value: selectedCourseModal?.SKU,
                        valueVariant: "body1" as const,
                      },
                      {
                        label: "Code",
                        value: selectedCourseModal?.code,
                        valueVariant: "body1" as const,
                      },
                      {
                        label: "Version",
                        value: selectedCourseModal?.version,
                        valueVariant: "body1" as const,
                      },
                    ].map((row) => (
                      <Box
                        key={row.label}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 4,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontSize: "16px" }}>
                          {row.label}
                        </Typography>
                        <Typography
                          variant={row.valueVariant}
                          sx={{
                            fontSize: "16px",
                            fontWeight: "600",
                            textAlign: "right",
                            wordBreak: "break-word",
                            wordWrap: "break-word",
                            maxWidth: "65%",
                          }}
                        >
                          {row.value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: "column",
                      width: "100%",
                      backgroundColor: gray[100],
                      borderRadius: "8px",
                      padding: "8px",
                      mt: 1,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "14px",
                        fontWeight: "500",
                        display: "block",
                      }}
                    >
                      Note
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: "14px" }}>
                      {selectedCourseModal?.note}
                    </Typography>
                  </Box>
                </Box>
              }
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: "white",
                    color: "text.primary",
                    boxShadow: 2,
                    borderRadius: 2,
                    p: 2,
                    width: 400,
                    maxWidth: 500,
                  },
                },
              }}
            >
              <Typography
                variant="body1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigateToAssignmentUploads(row);
                }}
                onMouseEnter={() => {
                  setCurrentCourse(row.assignmentUid);
                }}
                onMouseLeave={() => {
                  setCurrentCourse(null);
                }}
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  color: brand[600],
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {row.title}
              </Typography>
            </Tooltip>
          </Box>
        ),
      },
      {
        id: "code",
        label: "Code",
        sortable: true,
      },
      {
        id: "packageType",
        label: "Type",
        sortable: true,
      },
      {
        id: "owner",
        label: "Owner",
        sortable: true,
      },
      {
        id: "assignedUsers",
        label: "Assigned",
        sortable: true,
      },
      ...(!portals.selectedPortal?.isMain
        ? [
            {
              id: "rules",
              label: " ",
              sortable: false,
              render: (row: any) => {
                const isInactive = row.status === Status.INACTIVE;
                return (
                  <Tooltip
                    title="Manage rules"
                    placement="bottom"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#0C111D",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "#0C111D",
                        },
                      },
                    }}
                  >
                    <span
                      style={{
                        cursor: isInactive ? "not-allowed" : undefined,
                      }}
                    >
                      <IconButton
                        disabled={isInactive}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigateToRules(row);
                        }}
                        sx={{
                          border: "1px solid #D0D5DD",
                          backgroundColor: "#FFFFFF",
                          borderRadius: "8px",
                          width: "40px",
                          height: "40px",
                          "&:hover": {
                            backgroundColor: "#FFFFFF",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "#F2F4F7",
                            borderColor: "#EAECF0",
                            cursor: "not-allowed",
                          },
                        }}
                      >
                        <RulesetIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              },
            },
          ]
        : []),
    ],
    [
      selectedCourseModal,
      setCurrentCourse,
      handleCourseNameChange,
      handleNavigateToAssignmentUploads,
      handleNavigateToRules,
      portals.selectedPortal?.isMain,
    ],
  );

  usePageMetadata(
    {
      title: "Assignment search",
      breadcrumbs: [
        {
          label: "Assignment management",
          path: `/portal-configuration/${portalUid}/assignment-search`,
        },
        {
          label: "Assignment search",
        },
      ],
    },
    [portalUid, portals.selectedPortal],
  );

  return (
    <>
      <DataTable
        data={portalsArray}
        columns={columns}
        totalCount={
          loading
            ? Math.max(assignmentResponse?.count ?? 0, page * limit)
            : (assignmentResponse?.count ?? 0)
        }
        page={page}
        rowsPerPage={limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsChange}
        sort={sort}
        onSortChange={handleSortChange}
        search={assignmentSearch}
        onSearchChange={setAssignmentSearch}
        searchPlaceholder="Search by title or code"
        isLoading={loading}
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Courses</Typography>
            <Box
              minWidth={24}
              height={24}
              px={0.75}
              borderRadius="12px"
              bgcolor="#EFF8FF"
              border={"1px solid #B2DDFF"}
              display="flex"
              justifyContent="center"
              alignItems="center"
              boxSizing="border-box"
            >
              <Typography color="#175CD3" fontSize={14}>
                {assignmentResponse?.count ?? 0}
              </Typography>
            </Box>
          </Box>
        }
        actions={
          <Stack direction={"row"} spacing={2}>
            <Button
              onClick={() => {
                navigate(
                  `/portal-configuration/${portalUid}/assignment-search/group-cards`,
                );
              }}
              sx={{
                backgroundColor: `${brand[600]} !important`,
                color: `white`,
                borderRadius: "12px",
                padding: "16px",
                "&:hover": {
                  bgcolor: `${brand[700]} !important`,
                },
              }}
            >
              Group cards
            </Button>
            <Button
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "16px",
                border: "1px solid #D0D5DD",
              }}
              startIcon={
                <FilterListIcon
                  sx={{
                    color: "black",
                  }}
                />
              }
              onClick={() => {
                setShowFilter(!showFilter);
              }}
            >
              Filter
            </Button>
          </Stack>
        }
        headerContent={
          !hasSeletedFilter ? (
            <Collapse in={!hasSeletedFilter} timeout="auto" unmountOnExit>
              <Divider sx={{ pt: 2 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pt: 2,
                  pb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <Typography sx={{ whiteSpace: "nowrap" }} fontWeight={"bold"}>
                    {`Filter Applied:`}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                    }}
                  >
                    {showChips && renderChips()}
                  </Box>
                </Box>

                <Button
                  onClick={() => clearFilters()}
                  sx={{
                    color: brand[600],
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Clear All
                </Button>
              </Box>
            </Collapse>
          ) : null
        }
      />
      {courseToggleModal && selectedCourseModal && (
        <ConfirmModal
          openConfirmModal={courseToggleModal}
          setOpenConfirmModal={setCourseToggleModal}
          confirmButtonClick={confirmModalButtonClick}
          modalTitle={selectedCourseModal.title}
          modalSubtitle="Are you sure you want to turn it off?"
          handleModalConfirm={handleModalConfirm}
          modalLoading={modalLoading}
        />
      )}
      <AssignmentsFilter
        filtersList={filtersListOptimized}
        clearFilters={clearFilters}
        setShowFilter={setShowFilter}
        showFilter={showFilter}
        handleCheckboxChange={handleCheckboxChange}
        saveFilters={saveFilters}
        disableClearAll={hasSeletedFilter}
        onClose={handleFilterDrawerClose}
      />
    </>
  );
};
